<script setup>
import { computed } from "vue";
import { useStore } from "vuex";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import { pushUrl } from "common/utils/browser";
import { trackPDPPageVisit } from "components/pod/pod-build-order/utils/ga_tracker";
import { trackCompositionClick } from "../utils/tracker";

const props = defineProps({
  compositions: {
    type: Object,
    required: true
  },
  selectedComposition: {
    type: Object,
    required: true
  }
});
const store = useStore();

const variantPrice = composition => {
  const variantIds = composition.variants.map(variant => variant.id);
  return store.getters["product/variantPriceForVariants"](composition.id, variantIds);
};

const isSingleProductPage = computed(() => store.getters["product/isSingleProductPage"]);
const selectedProduct = computed(() => store.getters["product/selectedProduct"]);
const compositionsInCart = computed(() => store.getters["checkout/compositions"]);

const handleColorSelect = composition => {
  store.dispatch("product/handleCompositionSelect", composition).then(() => {
    trackPDPPageVisit(selectedProduct.value, composition, variantPrice(composition));
  });
  trackCompositionClick(composition, selectedProduct.value, compositionsInCart.value);
  if (isSingleProductPage.value && composition.madePdpUrl) {
    pushUrl(composition.madePdpUrl, true);
  }
};

const compositionsQtys = computed(() => store.getters["product/compositionsQtys"]);

const compositionColorStyle = ({ swatchUrl, colorHex }) => ({
  background: swatchUrl ? `url(${swatchUrl})` : colorHex
});
</script>

<template>
  <div class="product-colors">
    <h4 class="product-colors__color-name">Color: {{ selectedComposition.colorName }}</h4>
    <div class="product-colors__selector">
      <button
        v-for="composition in props.compositions"
        :key="composition.id"
        type="button"
        class="product-colors__item"
        :class="{ 'product-colors__item--selected': composition.id === selectedComposition.id }"
        :style="compositionColorStyle(composition)"
        @click="handleColorSelect(composition)"
      >
        <span v-if="compositionsQtys[composition.id]" class="product-colors__count">
          {{ compositionsQtys[composition.id] }}
        </span>
        <UseSvg
          v-if="compositionsQtys[composition.id]"
          class="product-colors__item-icon"
          icon="check-empty"
          type="print"
        />
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/mixin";
@import "frontend/init/pod/settings";

.product-colors {
  margin: 0 auto;

  &__color-name {
    color: $black;
    font-size: 15px;
    font-weight: 500;
    text-align: center;

    @include with-tablet {
      text-align: left;
    }
  }

  &__selector {
    display: flex;
    overflow: auto;
    padding: 6px 8px 16px;
    gap: 8px;
    margin-top: 8px;
  }

  &__item {
    color: $black;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    outline: 0;
    margin: auto;
    border: 2px solid $digital-assets-gray-modern-200;
    flex-shrink: 0;
    padding: 0 8px;
    position: relative;

    &--selected {
      box-shadow: 0 0 0 2px $black;
    }
  }

  &__item-icon {
    width: 18px;
    height: 14px;
  }

  &__count {
    position: absolute;
    top: -6px;
    left: -6px;
    min-width: 16px;
    height: 16px;
    line-height: 16px;
    background: $blue;
    border-radius: 8px;
    font-size: 9px;
    font-weight: 700;
    color: $white;
    padding: 0 4px;
    text-align: center;
  }
}
</style>
